import { useEffect, useState } from 'react';
import { useSession, type SessionState } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C22 · somebody's profile picture, as something an `<img>` can use.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A blob URL, for the reason every other file read in this product uses one:** an `<img src>`
 * cannot carry an Authorization header, and putting the token in a query string writes it into
 * every access log. So the bytes come through the session and are handed to the browser as an
 * object URL.
 *
 * ⚠️ **`avatarUrl` is a PATH the server derived, not a host.** `/files/avatar/<user>` — the client
 * resolves it against its own API base, which is the same rule the attachment contract states.
 *
 * ⚠️⚠️ **ONE CACHE FOR THE WHOLE APP, KEYED ON THE PATH — and a list is why.** A task list of fifty
 * rows assigned to six people is six pictures, not fifty requests; the people table, the board, the
 * sidebar chip and the project panels all draw the same handful of faces on the same screen. A
 * per-component fetch would re-request each of them per mount, and the flicker on every re-render
 * would be the visible half of it.
 *
 * ⚠️ **Nothing is ever revoked.** An object URL pins its bytes until it is, and the cache is
 * deliberately process-wide and permanent: a picture is at most 2 MB, there are as many as there
 * are colleagues, and the alternative — revoking when the last component unmounts — needs reference
 * counting that is wrong the first time somebody renders two lists of the same people. A reload
 * clears it, and a changed picture changes nothing here because the path is stable: see `bust`.
 */
const CACHE = new Map<string, string>();
const PENDING = new Map<string, Promise<string | null>>();

/**
 * ⚠⚠️ **THE HOOKS HAVE TO BE TOLD, NOT JUST THE CACHE.** A module-level counter that nothing
 * subscribes to changes nothing on screen: the sidebar chip is not re-rendering because somebody
 * replaced their picture on the account screen. The first version cleared the map and the old face
 * stayed up until a reload.
 */
const LISTENERS = new Set<() => void>();

/**
 * Forget every cached picture.
 *
 * ⚠️ **Called after an avatar upload or removal, because the PATH DOES NOT CHANGE when the picture
 * does.** `/files/avatar/<user>` is stable by design — it is derived from the user id, not from the
 * content hash — so without this the sidebar would keep drawing the old face until a reload.
 */
export function forgetAvatars(): void {
  CACHE.clear();
  PENDING.clear();
  for (const listener of LISTENERS) listener();
}

async function load(session: SessionState, path: string): Promise<string | null> {
  const cached = CACHE.get(path);
  if (cached !== undefined) return cached;

  /* ⚠️ In-flight requests are shared. Without this, ten rows mounting in the same tick each start
   * their own fetch for the same picture — the cache only helps the eleventh. */
  const pending = PENDING.get(path);
  if (pending !== undefined) return pending;

  const work = (async () => {
    /**
     * C42 · ⚠️⚠️ **A WORKSPACE ICON HAS TO BE FETCHED *IN* ITS OWN WORKSPACE, AND THE PATH IS THE
     * ONLY PLACE THAT KNOWS WHICH.** The switcher draws an icon for every workspace somebody
     * belongs to, including ones they are not currently in — and `/files/workspace-icon/:id` is
     * workspace-scoped, so the default `x-workspace-id` would ask the wrong workspace and get a
     * 404. The caller is a member of the one being asked about, so the guard allows it.
     *
     * ⚠️ Derived from the path rather than passed in, so `useAvatars` keeps its signature and the
     * cache keeps its key. The path IS the address of a workspace-scoped resource; scoping the
     * request to it is the only reading that can be right.
     */
    const scoped = /^\/files\/workspace-icon\/([0-9a-fA-F-]{36})$/u.exec(path);
    const init =
      scoped === null ? undefined : { headers: { 'x-workspace-id': scoped[1] as string } };

    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) return null;
    const url = URL.createObjectURL(await response.blob());
    CACHE.set(path, url);
    return url;
  })().finally(() => {
    PENDING.delete(path);
  });

  PENDING.set(path, work);
  return work;
}

/** One person's picture. */
export function useAvatar(avatarUrl: string | null | undefined): string | undefined {
  return useAvatars([avatarUrl]).get(avatarUrl ?? '');
}

/**
 * Several people's pictures, by path.
 *
 * ⚠️ **Takes the list rather than being called in a loop**, because a hook cannot be: a row that
 * appears when a filter clears would change the number of hooks a list component runs.
 */
export function useAvatars(
  avatarUrls: readonly (string | null | undefined)[],
): ReadonlyMap<string, string> {
  const session = useSession();
  /**
   * ⚠⚠️ **THE ANSWER IS STATE, NOT A VALUE DERIVED FROM THE CACHE ON EVERY RENDER.** Two
   * things push it that way. A fresh `Map` per render is a new identity, and every screen here
   * builds its rows in a `useMemo` that reads this — so a new map per render defeats every one of
   * those memos. And deriving it from `CACHE` means the memo depends on module-level mutable state
   * that React cannot see, which is a dependency array that lies. Setting it from the effect that
   * fills the cache makes the identity change exactly when the contents do.
   */
  const [resolved, setResolved] = useState<ReadonlyMap<string, string>>(EMPTY);

  /* ⚠️ Sorted and joined: the DEPENDENCY is which pictures are wanted, not the order rows happen
   * to be in. Without this, re-sorting a list re-runs the effect for the same faces. */
  const key = [
    ...new Set(avatarUrls.filter((url): url is string => typeof url === 'string' && url !== '')),
  ]
    .sort()
    .join('|');

  useEffect(() => {
    let live = true;

    const settle = (): void => {
      const next = new Map<string, string>();
      for (const url of key === '' ? [] : key.split('|')) {
        const src = CACHE.get(url);
        if (src !== undefined) next.set(url, src);
      }
      /* ⚠️ Only when something actually changed, or this schedules a render per mount for a list
       * whose faces are all cached already. */
      setResolved((current) =>
        current.size === next.size && [...next].every(([url, src]) => current.get(url) === src)
          ? current
          : next,
      );
    };

    const wanted = key === '' ? [] : key.split('|');
    void Promise.all(wanted.map((url) => load(session, url))).then(() => {
      if (live) settle();
    });
    settle();

    /* Re-settle when a picture anywhere is replaced — see `forgetAvatars`. */
    const listener = (): void => {
      if (!live) return;
      void Promise.all(wanted.map((url) => load(session, url))).then(() => {
        if (live) settle();
      });
    };
    LISTENERS.add(listener);

    return () => {
      live = false;
      LISTENERS.delete(listener);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it re-fetches for ever. */
  }, [key]);

  return resolved;
}

/** One shared empty map, so a list with nobody to draw does not change identity per render. */
const EMPTY: ReadonlyMap<string, string> = new Map();
